import type {NavigateOptions} from '@github-ui/react-router'

/** The parts of a matched route a navigation context handler reads. */
export interface NavigationRouteMatch {
  handle: unknown
  params: Readonly<Record<string, string | undefined>>
}

export interface NavigationContextChange {
  /** The destination path, including search and hash. */
  destination: string
  /** The routes the current location matches, from the root route to the leaf. */
  from: NavigationRouteMatch[]
  /** The routes the destination matches, from the root route to the leaf. */
  to: NavigationRouteMatch[]
  options?: NavigateOptions
}

/**
 * - `unchanged`: the destination keeps the page's context, so the navigation continues as usual.
 * - `navigated`: the handler started the navigation itself, for example with a global navigation context refresh.
 * - `declined`: the destination has a different context the handler can't provide, so the page reloads.
 */
export type NavigationContextResult = 'unchanged' | 'navigated' | 'declined'

export type NavigationContextHandler = (change: NavigationContextChange) => NavigationContextResult

let registeredHandler: NavigationContextHandler | undefined
let registration: object | undefined

/**
 * Lets the page decide how in-app navigations that change its context are handled, such as moving to another
 * repository. Mounted React apps ask the handler before navigating.
 */
export function registerNavigationContextHandler(handler: NavigationContextHandler): () => void {
  // Each registration gets its own token, so cleaning up an older registration of the same function can't remove a
  // newer one.
  const token = {}
  registeredHandler = handler
  registration = token
  return () => {
    if (registration !== token) return
    registeredHandler = undefined
    registration = undefined
  }
}

export function hasNavigationContextHandler(): boolean {
  return !!registeredHandler
}

/** Asks the registered handler about a navigation. Returns `unchanged` without one and `declined` when it throws. */
export function handleNavigationContextChange(change: NavigationContextChange): NavigationContextResult {
  if (!registeredHandler) return 'unchanged'
  try {
    return registeredHandler(change)
  } catch {
    return 'declined'
  }
}
